import {
  FormField,
  MoveControls,
  Select,
  type MoveAction,
} from "@whitehaven/ui";
import { useEffect, useMemo, useState } from "react";
import {
  appointmentStatuses,
  schedulingMaximumDurationMinutes,
  schedulingMinimumDurationMinutes,
  type AppointmentStatus,
  type SchedulingResourceId,
} from "./scheduling-contract";
import type {
  AdminSchedulingAppointment,
  AdminSchedulingAppointmentMutationRequest,
  AdminSchedulingResource,
} from "./scheduling-portal-model";
import { formatSchedulingTimestamp } from "./scheduling-portal-model";
import { SchedulingPointerRescheduleControls } from "./SchedulingPointerRescheduleControls";

export interface SchedulingMoveResizeControlsProps {
  readonly appointment: AdminSchedulingAppointment;
  readonly resources: readonly AdminSchedulingResource[];
  readonly disabled?: boolean;
  readonly busy?: boolean;
  readonly disabledReason?: string;
  readonly onMutationRequest: (
    request: AdminSchedulingAppointmentMutationRequest,
  ) => void;
}

const timeActions: readonly MoveAction<"earlier" | "later">[] = [
  { id: "earlier", label: "15 minutes earlier" },
  { id: "later", label: "15 minutes later" },
];

function durationActions(
  durationMinutes: number,
): readonly MoveAction<"shorter" | "longer">[] {
  return [
    {
      id: "shorter",
      label: "15 minutes shorter",
      disabled: durationMinutes <= schedulingMinimumDurationMinutes,
      disabledReason: `Appointments cannot be shorter than ${schedulingMinimumDurationMinutes} minutes.`,
    },
    {
      id: "longer",
      label: "15 minutes longer",
      disabled: durationMinutes >= schedulingMaximumDurationMinutes,
      disabledReason: `Appointments cannot be longer than ${schedulingMaximumDurationMinutes} minutes.`,
    },
  ];
}

export function SchedulingMoveResizeControls({
  appointment,
  resources,
  disabled = false,
  busy = false,
  disabledReason = "Schedule editing is unavailable.",
  onMutationRequest,
}: SchedulingMoveResizeControlsProps) {
  const activeResources = useMemo(
    () => resources.filter((resource) => resource.status === "ACTIVE"),
    [resources],
  );
  const initialResourceId =
    appointment.resourceIds[0] ?? activeResources[0]?.id ?? null;
  const [resourceId, setResourceId] = useState<SchedulingResourceId | null>(
    initialResourceId,
  );
  const [appointmentStatus, setAppointmentStatus] = useState<AppointmentStatus>(
    appointment.status,
  );

  useEffect(() => {
    setResourceId(appointment.resourceIds[0] ?? activeResources[0]?.id ?? null);
    setAppointmentStatus(appointment.status);
  }, [
    appointment.appointmentId,
    appointment.revision,
    appointment.status,
    appointment.resourceIds,
    activeResources,
  ]);

  const itemLabel = `appointment ${appointment.appointmentId}`;
  const currentStart = formatSchedulingTimestamp(
    appointment.startsAt,
    appointment.timezone,
  );

  return (
    <div className="scheduling-appointment-controls">
      <SchedulingPointerRescheduleControls
        appointment={appointment}
        resources={resources}
        disabled={disabled}
        busy={busy}
        onMutationRequest={onMutationRequest}
      />
      <MoveControls
        id={`schedule-${appointment.appointmentId}-move-time`}
        itemLabel={itemLabel}
        legend="Move appointment time"
        instructions="Use the earlier and later buttons with a keyboard or pointer. Each confirmed change is 15 minutes."
        currentPosition={currentStart}
        actions={timeActions}
        busy={busy}
        disabled={disabled}
        disabledReason={disabledReason}
        live={false}
        onMoveRequest={(action) => {
          onMutationRequest({
            kind: "MOVE_TIME",
            appointmentId: appointment.appointmentId,
            expectedRevision: appointment.revision,
            deltaMinutes: action === "earlier" ? -15 : 15,
          });
        }}
      />
      <MoveControls
        id={`schedule-${appointment.appointmentId}-resize`}
        itemLabel={itemLabel}
        legend="Resize appointment duration"
        instructions="Use the shorter and longer buttons with a keyboard or pointer. Duration changes in 15-minute increments."
        currentPosition={`${appointment.durationMinutes} minutes`}
        currentPositionLabel="Current duration"
        actions={durationActions(appointment.durationMinutes)}
        busy={busy}
        disabled={disabled}
        disabledReason={disabledReason}
        live={false}
        onMoveRequest={(action) => {
          onMutationRequest({
            kind: "RESIZE",
            appointmentId: appointment.appointmentId,
            expectedRevision: appointment.revision,
            deltaMinutes: action === "shorter" ? -15 : 15,
          });
        }}
      />
      <MoveControls
        id={`schedule-${appointment.appointmentId}-move-resource`}
        itemLabel={itemLabel}
        legend="Move appointment to a resource"
        instructions="Choose an active resource, then use the apply button. This control does not require dragging."
        currentPosition={
          resources.find((resource) =>
            appointment.resourceIds.includes(resource.id),
          )?.displayLabel ?? "Unassigned"
        }
        actions={[
          {
            id: "apply-resource",
            label: "Apply resource",
            disabled:
              resourceId === null ||
              appointment.resourceIds.includes(resourceId),
            disabledReason:
              resourceId === null
                ? "No active resource is available."
                : "Choose a different resource.",
          },
        ]}
        busy={busy}
        disabled={disabled}
        disabledReason={disabledReason}
        live={false}
        onMoveRequest={() => {
          if (!resourceId) return;
          onMutationRequest({
            kind: "MOVE_RESOURCE",
            appointmentId: appointment.appointmentId,
            expectedRevision: appointment.revision,
            resourceId,
          });
        }}
      >
        <FormField label="Destination resource">
          {(controlProps) => (
            <Select
              {...controlProps}
              value={resourceId ?? ""}
              disabled={busy || disabled}
              onChange={(event) => {
                setResourceId(
                  event.currentTarget.value as SchedulingResourceId,
                );
              }}
            >
              {activeResources.length === 0 && (
                <option value="">No active resources</option>
              )}
              {activeResources.map((resource) => (
                <option value={resource.id} key={resource.id}>
                  {resource.displayLabel} ({resource.kind.replaceAll("_", " ")})
                </option>
              ))}
            </Select>
          )}
        </FormField>
      </MoveControls>
      <MoveControls
        id={`schedule-${appointment.appointmentId}-set-status`}
        itemLabel={itemLabel}
        legend="Change appointment status"
        instructions="Choose the confirmed workflow status, then apply it."
        currentPosition={appointment.status.replaceAll("_", " ")}
        actions={[
          {
            id: "apply-status",
            label: "Apply status",
            disabled: appointmentStatus === appointment.status,
            disabledReason: "Choose a different status.",
          },
        ]}
        busy={busy}
        disabled={disabled}
        disabledReason={disabledReason}
        live={false}
        onMoveRequest={() => {
          onMutationRequest({
            kind: "SET_STATUS",
            appointmentId: appointment.appointmentId,
            expectedRevision: appointment.revision,
            status: appointmentStatus,
          });
        }}
      >
        <FormField label="Confirmed status">
          {(controlProps) => (
            <Select
              {...controlProps}
              value={appointmentStatus}
              disabled={busy || disabled}
              onChange={(event) => {
                setAppointmentStatus(
                  event.currentTarget.value as AppointmentStatus,
                );
              }}
            >
              {appointmentStatuses.map((candidate) => (
                <option value={candidate} key={candidate}>
                  {candidate.replaceAll("_", " ")}
                </option>
              ))}
            </Select>
          )}
        </FormField>
      </MoveControls>
    </div>
  );
}
